import React from 'react';
import { Type, Sliders, Eye } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров текста
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущего размера шрифта
  const fontSize = parseFloat(formData.fontSize) || 16;
  
  // Расчет текущей высоты строки
  const lineHeight = parseFloat(formData.lineHeight) || 1.5;
  
  // Расчет текущего расстояния между буквами
  const letterSpacing = parseFloat(formData.letterSpacing) || 0;
  
  // Обработчик изменения размера шрифта
  const handleFontSizeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      fontSize: newValue
    }));
  };
  
  // Обработчик изменения высоты строки
  const handleLineHeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      lineHeight: newValue
    }));
  };
  
  // Обработчик изменения межбуквенного расстояния
  const handleLetterSpacingChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      letterSpacing: newValue
    }));
  };
  
  // Предопределенные размеры шрифта для быстрого выбора
  const predefinedFontSizes = [
    { value: '12', label: '12px' },
    { value: '14', label: '14px' },
    { value: '16', label: '16px' },
    { value: '18', label: '18px' },
    { value: '20', label: '20px' }
  ];
  
  // Предопределенные семейства шрифтов для быстрого выбора
  const predefinedFontFamilies = [
    { value: 'Inter, system-ui, sans-serif', label: 'Inter' },
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
    { value: '"Roboto", sans-serif', label: 'Roboto' },
    { value: '"Times New Roman", Times, serif', label: 'Times New Roman' }
  ];
  
  // Функция расчета (прокси для клика по кнопке основной формы)
  const calculateText = () => {
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sliders className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка текста
      </h3>
      
      <div className="space-y-5">
        {/* Размер шрифта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="fontSizeSlider" className="text-gray-600 dark:text-gray-400">Размер шрифта</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {fontSize} {formData.fontSizeUnit}
            </span>
          </div>
          <input
            type="range"
            id="fontSizeSlider"
            min="8"
            max="32"
            step="1"
            value={fontSize}
            onChange={handleFontSizeChange}
            aria-label="Размер шрифта"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными размерами */}
          <div className="grid grid-cols-5 gap-1 mt-2">
            {predefinedFontSizes.map(size => (
              <button
                key={size.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, fontSize: size.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.fontSize === size.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {size.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Высота строки */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="lineHeightSlider" className="text-gray-600 dark:text-gray-400">Высота строки</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {lineHeight}
            </span>
          </div>
          <input
            type="range"
            id="lineHeightSlider"
            min="1"
            max="2.5"
            step="0.1"
            value={lineHeight}
            onChange={handleLineHeightChange}
            aria-label="Высота строки"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные высоты строк */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['1', '1.2', '1.5', '2'].map(height => (
              <button
                key={height}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, lineHeight: height }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.lineHeight === height 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {height}
              </button>
            ))}
          </div>
        </div>
        
        {/* Межбуквенное расстояние */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="letterSpacingSlider" className="text-gray-600 dark:text-gray-400">Межбуквенное расстояние</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {letterSpacing}em
            </span>
          </div>
          <input
            type="range"
            id="letterSpacingSlider"
            min="-0.05"
            max="0.2"
            step="0.01"
            value={letterSpacing}
            onChange={handleLetterSpacingChange}
            aria-label="Межбуквенное расстояние"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные значения межбуквенного расстояния */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['-0.03', '0', '0.05', '0.1'].map(spacing => (
              <button
                key={spacing}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, letterSpacing: spacing }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.letterSpacing === spacing 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {spacing}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор шрифта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Шрифт</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {predefinedFontFamilies.map(font => (
              <button
                key={font.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, fontFamily: font.value }))}
                className={`p-2 text-sm rounded-lg border transition-colors
                  ${formData.fontFamily === font.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                style={{ fontFamily: font.value }}
              >
                {font.label}
              </button>
            ))}
          </div>
        </div>

        {/* Переключатель цветовой схемы */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Цветовая схема</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, colorScheme: 'light' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.colorScheme === 'light' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Светлая
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, colorScheme: 'dark' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.colorScheme === 'dark' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Темная
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateText}
          icon={<Eye className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Просмотр с новыми параметрами
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;